import { useEffect, useState } from "react";
import { cn } from "@/lib/cn";
import { initials } from "@/lib/format";
import { resolveLogoDisplayUrl } from "@/lib/logoUpload";

interface AvatarProps {
  name: string | null | undefined;
  src?: string | null | undefined;
  size?: "sm" | "md" | "lg";
  className?: string;
  /**
   * Colour the circle from the name's first letter (Gmail-style) with white
   * initials, instead of the default flat accent tint.
   */
  colorful?: boolean;
}

const SIZES = {
  sm: "h-8 w-8 text-xs",
  md: "h-10 w-10 text-sm",
  lg: "h-12 w-12 text-base",
} as const;

/** Solid, white-legible palette; the name's first letter picks one deterministically. */
const AVATAR_COLORS = [
  "#D32F2F", "#C2185B", "#7B1FA2", "#512DA8", "#303F9F",
  "#1976D2", "#0288D1", "#0097A7", "#00796B", "#388E3C",
  "#689F38", "#F57C00", "#E64A19", "#5D4037", "#455A64",
];

function colorForName(name: string | null | undefined): string {
  const first = name?.trim()?.[0]?.toUpperCase() ?? "";
  const code = first.charCodeAt(0);
  const index = Number.isNaN(code) ? 0 : code % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
}

export function Avatar({ name, src, size = "md", className, colorful = false }: AvatarProps) {
  const [imageFailed, setImageFailed] = useState(false);
  const imageSrc = resolveLogoDisplayUrl(src);

  useEffect(() => {
    setImageFailed(false);
  }, [src]);

  if (imageSrc && !imageFailed) {
    return (
      <img
        src={imageSrc}
        alt={name ? `${name} logo` : "Logo"}
        onError={() => setImageFailed(true)}
        className={cn(
          "inline-block rounded-full object-cover bg-white",
          "border border-bridge-accent/30",
          SIZES[size],
          className,
        )}
      />
    );
  }

  return (
    <span
      style={colorful ? { backgroundColor: colorForName(name) } : undefined}
      className={cn(
        "inline-flex items-center justify-center rounded-full font-semibold",
        colorful
          ? "text-white"
          : "bg-bridge-accent/20 text-bridge-accentSoft border border-bridge-accent/30",
        SIZES[size],
        className,
      )}
    >
      {initials(name)}
    </span>
  );
}